import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LinearProgress } from '@mui/material';
import { ToastProvider, useToast } from './utils/useToast';
import { PanelMain, PanelRoot } from './PanelComerciales.styles';
import StickyHeader from './sections/StickyHeader/StickyHeader';
import QuickActions, { QuickActionId } from './sections/QuickActions/QuickActions';
import TeamsTable from './sections/TeamsTable/TeamsTable';
import CommercialsTable from './sections/CommercialsTable/CommercialsTable';
import VipTable from './sections/VipTable/VipTable';
import NotificationsTable from './sections/NotificationsTable/NotificationsTable';
import AuditLog from './sections/AuditLog/AuditLog';
import CreateTeamDrawer from './drawers/CreateTeamDrawer';
import BlockCommercialDrawer from './drawers/BlockCommercialDrawer';
import {
    Commercial,
    Team,
    VipAccount,
    sameSpec,
} from './PanelComerciales.types';
import {
    selectActiveTeamNames,
    selectAuditLog,
    selectCommercials,
    selectFlowTypes,
    selectNotifications,
    selectPanelLoading,
    selectPanelMeta,
    selectTeams,
    selectVips,
} from 'src/redux/panel_comerciales/panel_comerciales.selectors';
import {
    appendAuditLog,
    deactivateTeam,
    removeVip,
    renameTeam,
    upsertCommercial,
    upsertTeam,
    upsertVip,
} from 'src/redux/panel_comerciales/panel_comerciales.slice';
import {
    loadSnapshot,
    saveSellerConfigThunk,
    saveVipOverrideThunk,
} from 'src/redux/panel_comerciales/thunks';
import {
    SellerConfigPatch,
    VipOverridePatch,
} from 'src/redux/panel_comerciales/panel_comerciales.types';
import { buildAuditEntry } from './utils/audit';
import type { AppDispatch } from 'src/redux/store';

const STATE_BY_STATUS: Record<Commercial['status'], SellerConfigPatch['state']> = {
    Activo: 'activo',
    Inactivo: 'inactivo',
    Bloqueado: 'incapacidad',
};

const parseGoalAmount = (raw: string): number | undefined => {
    const numericGoal = Number(raw.replace(/[^0-9.]/g, ''));
    return !Number.isNaN(numericGoal) && numericGoal > 0 ? numericGoal : undefined;
};

const buildCommercialPatch = (
    prev: Commercial,
    next: Commercial,
    period: string,
): { patch: SellerConfigPatch; summaryParts: string[] } => {
    const patch: SellerConfigPatch = { period };
    const summaryParts: string[] = [];

    if (next.capacity !== prev.capacity) {
        patch.capacity_pct = next.capacity;
        summaryParts.push(`capacidad ${prev.capacity}% → ${next.capacity}%`);
    }
    if (!sameSpec(next.specialty, prev.specialty)) {
        patch.distribution = next.specialty;
        summaryParts.push('afinidad actualizada');
    }
    if (next.goal !== prev.goal) {
        const numericGoal = parseGoalAmount(next.goal);
        if (numericGoal !== undefined) {
            patch.goals = [{ type_goal: 'revenue', goal: numericGoal }];
            summaryParts.push(`meta ${prev.goal} → ${next.goal}`);
        }
    }
    if (next.team !== prev.team) {
        patch.team = next.team;
        summaryParts.push(`equipo ${prev.team} → ${next.team}`);
    }
    if (next.status !== prev.status) {
        patch.state = STATE_BY_STATUS[next.status];
        summaryParts.push(`estado ${prev.status} → ${next.status}`);
    }
    if (next.absence !== prev.absence) {
        summaryParts.push(`ausencia "${next.absence}"`);
    }
    if (typeof next.notes === 'string' && next.notes !== prev.notes) {
        patch.notes = next.notes;
    }

    return { patch, summaryParts };
};

const PanelComercialesInner: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();

    const meta = useSelector(selectPanelMeta);
    const flowTypes = useSelector(selectFlowTypes);
    const teams = useSelector(selectTeams);
    const commercials = useSelector(selectCommercials);
    const vips = useSelector(selectVips);
    const notifications = useSelector(selectNotifications);
    const auditLog = useSelector(selectAuditLog);
    const teamOptions = useSelector(selectActiveTeamNames);
    const loading = useSelector(selectPanelLoading);

    const [drawer, setDrawer] = useState<QuickActionId | null>(null);

    useEffect(() => {
        dispatch(loadSnapshot());
    }, [dispatch]);

    const teamNames = useMemo(() => teams.map((t) => t.name), [teams]);

    const recordAudit = useCallback(
        (action: string, detail: string) => {
            dispatch(appendAuditLog(buildAuditEntry(action, detail)));
        },
        [dispatch],
    );

    const handleQuickAction = (action: QuickActionId) => setDrawer(action);
    const closeDrawer = () => setDrawer(null);

    const handleTeamUpsert = (next: Team, prevName?: string) => {
        const isRename = Boolean(prevName) && prevName !== next.name;
        if (isRename && prevName) {
            dispatch(renameTeam({ from: prevName, to: next.name }));
            recordAudit('Renombró equipo', `${prevName} → ${next.name}`);
        }
        dispatch(upsertTeam(next));
        if (!isRename && !prevName) {
            recordAudit('Creó equipo', `${next.name} (líder ${next.leader}, meta ${next.goal})`);
            showToast({ message: 'Equipo creado', severity: 'success' });
        } else {
            showToast({ message: 'Equipo actualizado', severity: 'success' });
        }
    };

    const handleTeamDeactivate = (name: string) => {
        dispatch(deactivateTeam(name));
        recordAudit('Desactivó equipo', name);
        showToast({ message: `Equipo ${name} desactivado`, severity: 'info' });
    };

    const handleCommercialUpdate = (next: Commercial, prev: Commercial) => {
        dispatch(upsertCommercial(next));

        const { patch, summaryParts } = buildCommercialPatch(prev, next, meta.period);

        if (Object.keys(patch).length > 1) {
            dispatch(saveSellerConfigThunk({ sellerId: next.id, patch }));
        }

        if (summaryParts.length > 0) {
            recordAudit(
                'Actualizó comercial',
                `${next.name}: ${summaryParts.join(', ')}`,
            );
            showToast({ message: 'Cambios guardados', severity: 'success' });
        }
    };

    const handleVipAssign = (vip: VipAccount) => {
        dispatch(upsertVip(vip));
        const patch: VipOverridePatch = {
            seller_id: commercials.find((c) => c.name === vip.owner)?.id ?? 0,
            action: 'assign',
            validity: vip.validity,
            client_email: vip.email,
            client_name: vip.client,
        };
        dispatch(saveVipOverrideThunk({ clientId: vip.id, patch }));
        recordAudit('Asignó cuenta VIP', `${vip.client} → ${vip.owner}`);
        showToast({ message: 'VIP asignado', severity: 'success' });
    };

    const handleVipRevoke = (vip: VipAccount) => {
        dispatch(removeVip(vip.id));
        const patch: VipOverridePatch = {
            action: 'revoke',
        };
        dispatch(saveVipOverrideThunk({ clientId: vip.id, patch }));
        recordAudit('Revocó cuenta VIP', `${vip.client} (${vip.owner})`);
        showToast({ message: 'VIP revocado', severity: 'info' });
    };

    const handleBlockConfirm = (sellerId: number, patch: SellerConfigPatch, label: string) => {
        const seller = commercials.find((c) => c.id === sellerId);
        if (!seller) return;
        dispatch(
            upsertCommercial({
                ...seller,
                status: 'Bloqueado',
                active: false,
                absence: `${patch.state ?? ''} ${patch.state_since ?? ''} → ${patch.state_until ?? ''}`.trim(),
                tempBlockUntil: patch.state_until ?? undefined,
            }),
        );
        dispatch(saveSellerConfigThunk({ sellerId, patch }));
        recordAudit('Bloqueó comercial', label);
        showToast({ message: 'Bloqueo registrado', severity: 'warning' });
    };

    return (
        <PanelRoot>
            <StickyHeader />
            {loading && <LinearProgress sx={{ height: 2 }} />}
            <PanelMain>
                <QuickActions onAction={handleQuickAction} />

                <TeamsTable
                    teams={teams}
                    onTeamUpsert={handleTeamUpsert}
                    onTeamDeactivate={handleTeamDeactivate}
                />

                <CommercialsTable
                    commercials={commercials}
                    teamOptions={teamOptions}
                    flowTypes={flowTypes}
                    onCommercialUpdate={handleCommercialUpdate}
                />

                <VipTable
                    vips={vips}
                    commercials={commercials}
                    onAssign={handleVipAssign}
                    onRevoke={handleVipRevoke}
                />

                <NotificationsTable notifications={notifications} />

                <AuditLog entries={auditLog} />
            </PanelMain>

            <CreateTeamDrawer
                open={drawer === 'create-team'}
                onClose={closeDrawer}
                existingNames={teamNames}
                onSave={(t) => handleTeamUpsert(t)}
            />
            <BlockCommercialDrawer
                open={drawer === 'block-commercial'}
                onClose={closeDrawer}
                commercials={commercials}
                period={meta.period}
                onConfirm={handleBlockConfirm}
            />
        </PanelRoot>
    );
};

const PanelComerciales: React.FC = () => (
    <ToastProvider>
        <PanelComercialesInner />
    </ToastProvider>
);

export default PanelComerciales;
